import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Delhi", "Goa",
  "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh",
  "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan",
  "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
];

export default function CompanySetup() {
  const { company, refreshCompany } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "", gstin: "", state: "Maharashtra", address: "", phone: "", email: "", pan: "", einvoice_enabled: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (company) setForm(company);
  }, [company]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/companies", form);
      await refreshCompany();
      toast.success("Company saved");
      nav("/app");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6 lb-fade" data-testid="company-setup-page">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">Company setup</div>
        <h1 className="font-display font-extrabold text-3xl tracking-tight">Your business details</h1>
        <p className="text-neutral-600 mt-2 text-sm">These appear on every invoice and are used for GST filings.</p>
      </div>

      <form onSubmit={submit} className="lb-card p-6 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="lb-label">Legal Name</label>
            <input className="lb-input" required data-testid="cs-name"
                   value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <label className="lb-label">GSTIN</label>
            <input className="lb-input font-mono uppercase" required maxLength={15} data-testid="cs-gstin"
                   value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })} />
          </div>
          <div>
            <label className="lb-label">State</label>
            <select className="lb-select" required data-testid="cs-state"
                    value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })}>
              {STATES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="lb-label">PAN</label>
            <input className="lb-input font-mono uppercase" maxLength={10} data-testid="cs-pan"
                   value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })} />
          </div>
          <div>
            <label className="lb-label">Phone</label>
            <input className="lb-input" data-testid="cs-phone"
                   value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div className="col-span-2">
            <label className="lb-label">Email</label>
            <input className="lb-input" type="email" data-testid="cs-email"
                   value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div className="col-span-2">
            <label className="lb-label">Address</label>
            <textarea className="lb-textarea" rows={2} data-testid="cs-address"
                      value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </div>
          <label className="col-span-2 flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="cs-einvoice"
                   checked={form.einvoice_enabled}
                   onChange={(e) => setForm({ ...form, einvoice_enabled: e.target.checked })} />
            Enable e-Invoice auto-generation (B2B ≥ ₹50,000)
          </label>
        </div>
        <div className="flex gap-3 pt-2">
          <button className="lb-btn" disabled={saving} data-testid="cs-submit">
            {saving ? "Saving…" : "Save Company →"}
          </button>
        </div>
      </form>
    </div>
  );
}
